import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import {
  AuthInterface,
  SignUpInput,
  PlayersInterface,
  GetPlayersInput,
  GetPlayersOutput,
  PlayerOutput,
  SignInInput,
  SignUpOutput,
  SignInOutput,
  RefreshTokenOutput,
  MatchesInterface,
  GetMatchesInput,
  GetMatchesOutput,
  MatchOutput,
  MatchDetailedDto,
  GetMatchInput,
  GetPlayerInput,
  WithToken,
  CitiesInterface,
  TournamentsInterface,
  GetTournamentsByCityInput,
  ExtendedTournamentOutput,
  GetTournamentInput,
  UsersInterface,
  TournamentOutput,
  UserOutput,
  CityOutput,
  MatchStatus,
  GetMatchesCountsByDateInput,
  MatchesCountsByDateOutput,
  TeamsCategoriesInterface,
  TeamCategoryOutput,
  GetTeamCategoriesByTournamentInput,
  GetTournamentTeamsInput,
  TeamOutput,
  StatsInterface,
  GetMatchStatsInput,
  GetMatchLogsInput,
  MatchLog,
  TeamStatisticsInterface,
  GetTeamStatisticsInput,
  TeamStatisticOutput,
  PlayoffsInterface,
  GetPlayoffMatchesInput,
  PlayoffMatchOutput,
  TeamsInterface,
  GetTeamsInput,
  GetTeamsOutput,
  GetTeamInput,
  AwardOutput,
  GetPlayoffInput,
  PlayoffOutput,
  PlayerStatisticOutput,
  GetTeamMatchHistoryInput,
  CompleteTeamStatisticsOutput,
  TeamStatsticsByTournamentOutput,
  LastMatchOutput,
  TeamMatchHistoryOutput,
  GetLeaderboardOutput,
  GetTeamLeaderboardInput,
  GetTeamLeaderboardOutput,
  GetDetailedTeamStatisticsInput,
  DetailedTeamStatisticsOutput,
  GetLeaderboardInput,
  GetTeamMatchesInput,
  PlayerStatisticsByTournamentOutput,
  GetPlayerStatisticsInput,
  GetPlayersStatisticsInput,
  PlayerLastMatchOutput,
  GetPlayerByUserIdInput,
  MatchData,
  CoachesInterface,
  GetCoachInput,
  GetCoachTeamsInput,
  Coach,
  CoachTeamsOutput,
  VenuesInterface,
  VenueOutput,
  GetVenueInput,
  SimpleSignUpInput,
  GetTeamsAllInput,
  GetTeamsAllOutput,
} from "./models";
import { trackAnalyticsEvent } from "~@/analytics/amplitude";
import { ANALYTICS_EVENTS } from "~@/analytics/events";

declare module "axios" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface InternalAxiosRequestConfig {
    metadata?: {
      startTime?: number;
      analyticsName?: string;
    };
  }
}

const API_BASE_URL = process.env.REACT_APP_JUMP_API_ENDPOINT;
const IS_PRODUCTION = !API_BASE_URL?.includes("dev");

const authAxiosInstance = axios.create({
  timeout: 10000,
});

export type AuthEvent =
  | { type: "refreshed"; data: RefreshTokenOutput }
  | { type: "expired" };

const authListeners = new Set<(event: AuthEvent) => void>();

// Lets AuthProvider keep its state in sync with refreshes triggered here
export function subscribeAuthEvents(listener: (event: AuthEvent) => void) {
  authListeners.add(listener);
  return () => {
    authListeners.delete(listener);
  };
}

function emitAuthEvent(event: AuthEvent) {
  authListeners.forEach((listener) => listener(event));
}

function clearStoredSession() {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("userData");
}

let refreshPromise: Promise<RefreshTokenOutput> | null = null;

// Single refresh for the whole app: concurrent callers share one request,
// so a rotated refresh token is never sent twice.
export async function refreshAccessToken(): Promise<RefreshTokenOutput> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      clearStoredSession();
      emitAuthEvent({ type: "expired" });
      throw new Error("No refresh token");
    }

    const authUrl = IS_PRODUCTION
      ? "https://auth.besau.kz/api/v2/auth/user/refresh-token"
      : "https://auth.dev.besau.kz/api/v2/auth/user/refresh-token";

    try {
      const { data } = await authAxiosInstance.post<RefreshTokenOutput>(
        authUrl,
        { refreshToken },
      );
      localStorage.setItem("accessToken", data.token);
      localStorage.setItem("refreshToken", data.refreshToken);
      emitAuthEvent({ type: "refreshed", data });
      return data;
    } catch (error) {
      // Only a 4xx means the refresh token is dead. Network errors and 5xx
      // keep the session so the user isn't logged out by a flaky connection.
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status !== undefined && status >= 400 && status < 500) {
        clearStoredSession();
        emitAuthEvent({ type: "expired" });
      }
      throw error;
    }
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

const recordApiAnalytics = (
  config?: InternalAxiosRequestConfig,
  status?: number,
  error?: unknown,
) => {
  if (!config) {
    return;
  }

  const duration =
    typeof config.metadata?.startTime === "number"
      ? Math.max(Date.now() - config.metadata.startTime, 0)
      : undefined;

  trackAnalyticsEvent(ANALYTICS_EVENTS.API_RESPONSE, {
    endpoint: config.metadata?.analyticsName || config.url,
    method: config.method?.toUpperCase(),
    status_code: status ?? null,
    duration_ms: duration,
    success: !error,
    has_error: Boolean(error),
    error_message:
      error instanceof Error
        ? error.message
        : typeof error === "string"
          ? error
          : undefined,
  });
};

abstract class ApiService {
  protected readonly apiClient: AxiosInstance;
  private readonly clientInstance: AxiosInstance;
  protected readonly endpoint: string;

  constructor(endpoint: string) {
    this.endpoint = endpoint;
    this.apiClient = axios.create({
      baseURL: `${API_BASE_URL}${endpoint}`,
      timeout: 10000,
    });
    this.clientInstance = this.apiClient;

    this.apiClient.interceptors.request.use((config) => {
      const existingMetadata = config.metadata ?? {};
      config.metadata = {
        ...existingMetadata,
        startTime: Date.now(),
        analyticsName:
          existingMetadata.analyticsName ??
          `${this.endpoint}${config.url ?? ""}`,
      };
      return config;
    });

    this.apiClient.interceptors.response.use(
      (response) => {
        recordApiAnalytics(response.config, response.status);
        return response;
      },
      async (error) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean;
        };

        if (
          error.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          const refreshToken = localStorage.getItem("refreshToken");
          if (!refreshToken) {
            recordApiAnalytics(originalRequest, error.response?.status, error);
            return Promise.reject(error);
          }

          try {
            const { token } = await refreshAccessToken();

            originalRequest.headers.Authorization = `Bearer ${token}`;
            return this.clientInstance(originalRequest);
          } catch (refreshError) {
            recordApiAnalytics(
              originalRequest,
              error.response?.status,
              refreshError,
            );
            return Promise.reject(refreshError);
          }
        }

        const isPlayerByUserId404 =
          error.config?.url?.includes("/by-user-id/") &&
          error.response?.status === 404;

        if (isPlayerByUserId404) {
          return Promise.resolve({
            status: 404,
            statusText: "Not Found",
            data: null,
            headers: error.response?.headers || {},
            config: error.config,
          });
        }

        recordApiAnalytics(originalRequest, error.response?.status, error);
        return Promise.reject(error);
      },
    );
  }

  protected createAuthHeaders(token: string) {
    return {
      Authorization: `Bearer ${token}`,
    };
  }
}

class AuthService extends ApiService implements AuthInterface {
  constructor() {
    super("auth");
  }

  async signUp(data: SignUpInput): Promise<AxiosResponse<SignUpOutput>> {
    return this.apiClient.post("signup", data);
  }

  async simpleSignUp(
    data: SimpleSignUpInput,
  ): Promise<AxiosResponse<SignUpOutput>> {
    const userData = {
      login: data.nickname,
      email: data.email,
    };
    const authUrl = IS_PRODUCTION
      ? "https://auth.besau.kz/api/v2/auth/user/sign-up"
      : "https://auth.dev.besau.kz/api/v2/auth/user/sign-up";
    return this.apiClient.post(authUrl, userData);
  }

  async signIn(data: SignInInput): Promise<AxiosResponse<SignInOutput>> {
    const authUrl = IS_PRODUCTION
      ? "https://auth.besau.kz/api/v2/auth/user/verify"
      : "https://auth.dev.besau.kz/api/v2/auth/user/verify";
    return this.apiClient.put(authUrl, data);
  }
}

class CitiesService extends ApiService implements CitiesInterface {
  constructor() {
    super("cities");
  }

  async getCities(): Promise<AxiosResponse<CityOutput[]>> {
    return this.apiClient.get("");
  }
}

class PlayersService extends ApiService implements PlayersInterface {
  constructor() {
    super("players");
  }

  async getPlayerLastMatch({
    id,
    token,
  }: WithToken<GetPlayerInput>): Promise<AxiosResponse<PlayerLastMatchOutput>> {
    return this.apiClient.get(`${id}/last-match`, {
      headers: this.createAuthHeaders(token),
    });
  }

  async getPlayerStatisticsByTournament({
    id,
    token,
  }: WithToken<GetPlayerInput>): Promise<
    AxiosResponse<PlayerStatisticsByTournamentOutput[]>
  > {
    return this.apiClient.get(`${id}/stats-by-tournament`, {
      headers: this.createAuthHeaders(token),
    });
  }

  async getLeaderboard({
    ...params
  }: GetLeaderboardInput): Promise<AxiosResponse<GetLeaderboardOutput>> {
    return this.apiClient.get("leaderboard", { params });
  }

  async getTeamLeaderboard({
    ...params
  }: GetTeamLeaderboardInput): Promise<AxiosResponse<GetTeamLeaderboardOutput>> {
    const token = localStorage.getItem("accessToken");
    return this.apiClient.get("leaderboard/team", {
      params,
      ...(token ? { headers: this.createAuthHeaders(token) } : {}),
    });
  }

  async getPlayer({
    id,
    token,
  }: WithToken<GetPlayerInput>): Promise<AxiosResponse<PlayerOutput>> {
    return this.apiClient.get(`${id}/profile`, {
      headers: this.createAuthHeaders(token),
    });
  }

  async getPlayerByUserId({
    keycloakUserId,
    token,
  }: WithToken<GetPlayerByUserIdInput>): Promise<AxiosResponse<PlayerOutput>> {
    return this.apiClient.get(`by-user-id/${keycloakUserId}`, {
      headers: this.createAuthHeaders(token),
      validateStatus: (status) => status === 200 || status === 404,
    });
  }

  async getPlayerStatistics({
    id,
    token,
    ...params
  }: WithToken<GetPlayerStatisticsInput>): Promise<
    AxiosResponse<PlayerStatisticOutput>
  > {
    return this.apiClient.get(`${id}/stats-and-matches`, {
      params,
      headers: this.createAuthHeaders(token),
    });
  }

  async getPlayersStatistics({
    token,
    ...params
  }: WithToken<GetPlayersStatisticsInput>): Promise<
    AxiosResponse<PlayerStatisticOutput[]>
  > {
    return this.apiClient.get("stats-and-matches", {
      params,
      headers: this.createAuthHeaders(token),
    });
  }

  async getPlayers({
    token,
    ...params
  }: WithToken<GetPlayersInput>): Promise<AxiosResponse<GetPlayersOutput>> {
    return this.apiClient.get("", {
      params,
      headers: this.createAuthHeaders(token),
    });
  }
}

class TeamsService extends ApiService implements TeamsInterface {
  constructor() {
    super("teams");
  }

  async getTeam({
    id,
    token,
  }: WithToken<GetTeamInput>): Promise<AxiosResponse<TeamOutput>> {
    return this.apiClient.get(id.toString(), {
      headers: this.createAuthHeaders(token),
    });
  }

  async getTeams({
    ...params
  }: GetTeamsInput): Promise<AxiosResponse<GetTeamsOutput>> {
    return this.apiClient.get("", { params });
  }

  async getTeamsAll({
    ...params
  }: GetTeamsAllInput): Promise<AxiosResponse<GetTeamsAllOutput>> {
    return this.apiClient.get("/all", { params });
  }

  async getTeamAwards({
    id,
    token,
  }: WithToken<GetTeamInput>): Promise<AxiosResponse<AwardOutput[]>> {
    return this.apiClient.get(`${id}/awards`, {
      headers: this.createAuthHeaders(token),
    });
  }
}

class MatchesService extends ApiService implements MatchesInterface {
  constructor() {
    super("matches");
  }

  async getMatch({
    matchId,
    token,
  }: WithToken<GetMatchInput>): Promise<AxiosResponse<MatchOutput>> {
    return this.apiClient.get(matchId.toString(), {
      headers: this.createAuthHeaders(token),
    });
  }

  async getMatchDetailed({
    matchId,
    token,
  }: WithToken<GetMatchInput>): Promise<AxiosResponse<MatchDetailedDto>> {
    return this.apiClient.get(matchId.toString(), {
      headers: this.createAuthHeaders(token),
    });
  }

  async getMatches({
    ...params
  }: GetMatchesInput): Promise<AxiosResponse<GetMatchesOutput>> {
    return this.apiClient.get("", { params });
  }

  async getMatchStatuses(): Promise<AxiosResponse<MatchStatus[]>> {
    return this.apiClient.get("matchStatusTypes");
  }

  async getMatchesCountsByDate({
    ...params
  }: GetMatchesCountsByDateInput): Promise<
    AxiosResponse<MatchesCountsByDateOutput>
  > {
    return this.apiClient.get("count-by-date", { params });
  }
}

class TournamentsService extends ApiService implements TournamentsInterface {
  constructor() {
    super("tournaments");
  }

  async getTournament({
    id,
    token,
  }: WithToken<GetTournamentInput>): Promise<
    AxiosResponse<ExtendedTournamentOutput>
  > {
    return this.apiClient.get(id.toString(), {
      headers: this.createAuthHeaders(token),
    });
  }

  async getTournaments(
    token: string,
  ): Promise<AxiosResponse<ExtendedTournamentOutput[]>> {
    return this.apiClient.get("", {
      headers: this.createAuthHeaders(token),
    });
  }

  async getTournamentsByCity({
    ...params
  }: GetTournamentsByCityInput): Promise<AxiosResponse<TournamentOutput[]>> {
    return this.apiClient.get("all", { params });
  }

  async getTournamentTeams({
    tournamentId,
    token,
  }: WithToken<GetTournamentTeamsInput>): Promise<AxiosResponse<TeamOutput[]>> {
    return this.apiClient.get(`${tournamentId}/teams`, {
      headers: this.createAuthHeaders(token),
    });
  }
}

class UsersService extends ApiService implements UsersInterface {
  constructor() {
    super("users");
  }

  async getUsers(token: string): Promise<AxiosResponse<UserOutput[]>> {
    return this.apiClient.get("all", {
      headers: this.createAuthHeaders(token),
    });
  }
}

class TeamStatisticsService
  extends ApiService
  implements TeamStatisticsInterface
{
  constructor() {
    super("team-statistics");
  }

  async getTeamMatchesStatistics({
    teamId,
    token,
    ...params
  }: WithToken<GetTeamMatchesInput>): Promise<
    AxiosResponse<LastMatchOutput[]>
  > {
    return this.apiClient.get(`${teamId}/matches`, {
      params,
      headers: this.createAuthHeaders(token),
    });
  }

  async getDetailedTeamStatistics({
    token,
    ...params
  }: WithToken<GetDetailedTeamStatisticsInput>): Promise<
    AxiosResponse<DetailedTeamStatisticsOutput[]>
  > {
    return this.apiClient.get("detailed", {
      params,
      headers: this.createAuthHeaders(token),
    });
  }

  async getCompleteTeamStatistics({
    id,
    token,
  }: WithToken<GetTeamInput>): Promise<
    AxiosResponse<CompleteTeamStatisticsOutput>
  > {
    return this.apiClient.get(`${id}/complete-data`, {
      headers: this.createAuthHeaders(token),
    });
  }

  async getTeamStatisticsByTournament({
    id,
    token,
  }: WithToken<GetTeamInput>): Promise<
    AxiosResponse<TeamStatsticsByTournamentOutput[]>
  > {
    return this.apiClient.get(`${id}/tournaments`, {
      headers: this.createAuthHeaders(token),
    });
  }

  async getTeamMatchHistory({
    team1Id,
    team2Id,
    token,
  }: WithToken<GetTeamMatchHistoryInput>): Promise<
    AxiosResponse<TeamMatchHistoryOutput[]>
  > {
    return this.apiClient.get(`${team1Id}/vs/${team2Id}/matches`, {
      headers: this.createAuthHeaders(token),
    });
  }

  async getTeamLastMatch({
    id,
    token,
  }: WithToken<GetTeamInput>): Promise<AxiosResponse<LastMatchOutput>> {
    return this.apiClient.get(`${id}/last-match`, {
      headers: this.createAuthHeaders(token),
    });
  }

  async getTeamStatistics({
    ...params
  }: GetTeamStatisticsInput): Promise<AxiosResponse<TeamStatisticOutput[]>> {
    return this.apiClient.get("", { params });
  }
}

class PlayoffsService extends ApiService implements PlayoffsInterface {
  constructor() {
    super("playoff");
  }

  async getPlayoff({
    ...params
  }: GetPlayoffInput): Promise<AxiosResponse<PlayoffOutput[]>> {
    return this.apiClient.get("", { params });
  }

  async getPlayoffMatches({
    token,
    ...params
  }: WithToken<GetPlayoffMatchesInput>): Promise<
    AxiosResponse<PlayoffMatchOutput[]>
  > {
    return this.apiClient.get("matches", {
      params,
      headers: this.createAuthHeaders(token),
    });
  }
}

class TeamsCategoriesService
  extends ApiService
  implements TeamsCategoriesInterface
{
  constructor() {
    super("team-category");
  }

  async getTeamCategoriesByTournament({
    tournamentId,
  }: GetTeamCategoriesByTournamentInput): Promise<
    AxiosResponse<TeamCategoryOutput[]>
  > {
    return this.apiClient.get(`tournament/${tournamentId}`);
  }

  async getPlayoffWinners({
    teamCategoryId,
    token,
  }: WithToken<{ teamCategoryId: number }>): Promise<AxiosResponse<string[]>> {
    return this.apiClient.get(`${teamCategoryId}/playoff-winners`, {
      headers: this.createAuthHeaders(token),
    });
  }
}

class StatsService extends ApiService implements StatsInterface {
  constructor() {
    super("stat");
  }

  async getMatchStats({
    token,
    matchId,
  }: WithToken<GetMatchStatsInput>): Promise<AxiosResponse<Blob>> {
    return this.apiClient.get<Blob>(`pdf/match/${matchId}`, {
      responseType: "blob",
      headers: this.createAuthHeaders(token),
    });
  }

  async getMatchData({
    token,
    matchId,
  }: WithToken<GetMatchStatsInput>): Promise<AxiosResponse<MatchData>> {
    return this.apiClient.get(`pdf/data/${matchId}`, {
      headers: this.createAuthHeaders(token),
    });
  }

  async getMatchLogs({
    token,
    matchId,
  }: WithToken<GetMatchLogsInput>): Promise<AxiosResponse<MatchLog[]>> {
    return this.apiClient.get(`logs/match/${matchId}`, {
      headers: this.createAuthHeaders(token),
    });
  }
}

class LogsService extends ApiService {
  constructor() {
    super("logs");
  }

  async getMatchLogs({
    token,
    matchId,
  }: WithToken<GetMatchLogsInput>): Promise<AxiosResponse<MatchLog[]>> {
    return this.apiClient.get(`match/${matchId}`, {
      headers: this.createAuthHeaders(token),
    });
  }
}

class CoachService extends ApiService implements CoachesInterface {
  constructor() {
    super("coaches");
  }

  async getCoachProfile({
    id,
    token,
  }: WithToken<GetCoachInput>): Promise<AxiosResponse<Coach>> {
    return this.apiClient.get(`${id}/profile`, {
      headers: this.createAuthHeaders(token),
    });
  }

  async getCoachTeams({
    token,
    ...params
  }: WithToken<GetCoachTeamsInput>): Promise<
    AxiosResponse<CoachTeamsOutput[]>
  > {
    return this.apiClient.get("teams", {
      params,
      headers: this.createAuthHeaders(token),
    });
  }

  async getCoachByTeamId({
    teamId,
    token,
  }: WithToken<{ teamId: number }>): Promise<AxiosResponse<Coach>> {
    return this.apiClient.get("search", {
      params: { teamId },
      headers: this.createAuthHeaders(token),
    });
  }
}

class ParentsService extends ApiService {
  constructor() {
    super("parents");
  }

  async getParentsPlayers(
    token: string,
  ): Promise<AxiosResponse<PlayerOutput[]>> {
    return this.apiClient.get("players", {
      headers: this.createAuthHeaders(token),
    });
  }
}

class VenuesService extends ApiService implements VenuesInterface {
  constructor() {
    super("venues");
  }

  async getVenue({ id }: GetVenueInput): Promise<AxiosResponse<VenueOutput>> {
    return this.apiClient.get(id.toString());
  }

  async getVenues(token: string): Promise<AxiosResponse<VenueOutput[]>> {
    return this.apiClient.get("", {
      headers: this.createAuthHeaders(token),
    });
  }
}

const $api = Object.freeze({
  auth: new AuthService(),
  cities: new CitiesService(),
  logs: new LogsService(),
  players: new PlayersService(),
  parents: new ParentsService(),
  matches: new MatchesService(),
  tournaments: new TournamentsService(),
  users: new UsersService(),
  teamsCategories: new TeamsCategoriesService(),
  stats: new StatsService(),
  teamStatistics: new TeamStatisticsService(),
  playoff: new PlayoffsService(),
  team: new TeamsService(),
  coaches: new CoachService(),
  venues: new VenuesService(),
});

export default $api;
